<template>
  <div class="sci-view-container">
    <!-- Top Glassmorphic Navigation -->
    <header class="sci-header">
      <div class="sci-header-left">
        <button class="sci-btn-back" @click="home">
          <i class="fas fa-arrow-left"></i>
          <span>{{ $t('gestionale') || 'Gestionale' }}</span>
        </button>
        <div class="sci-title-group">
          <h1 class="sci-title">
            <i class="fas fa-paper-plane me-2 text-primary"></i>
            {{ $t('NEWSLETTER') || 'Newsletter & Comunicazioni Clienti' }}
          </h1>
          <p class="sci-subtitle">Invia promozioni, auguri per festività e comunicazioni al database iscritti</p>
        </div>
      </div>
    </header>

    <!-- Main Container -->
    <div class="container-fluid mt-4 pb-5">
      <div class="row">
        <!-- LEFT COLUMN: Preset and Saved Messages -->
        <div class="col-lg-3 col-md-4 mb-4">
          <div class="card shadow-sm h-100">
            <div class="card-header bg-dark text-white d-flex justify-content-between align-items-center">
              <h5 class="mb-0 fs-6">Modelli & Festività</h5>
              <button class="btn btn-sm btn-success py-1 px-2" @click="resetEditor">
                <i class="fas fa-plus"></i> Nuovo
              </button>
            </div>
            <div class="card-body p-0 list-container">
              <!-- Preset Templates Section -->
              <div class="bg-light p-2 border-bottom fw-bold text-uppercase fs-7 text-muted">
                Festività Preimpostate
              </div>
              <div class="list-group list-group-flush">
                <button
                  v-for="tpl in presetTemplates"
                  :key="tpl._id"
                  class="list-group-item list-group-item-action text-start py-2 px-3 align-items-center border-0 border-bottom"
                  :class="{ active: selectedTemplate && selectedTemplate._id === tpl._id }"
                  @click="loadTemplate(tpl)"
                >
                  <div class="d-flex w-100 justify-content-between">
                    <span class="fw-semibold text-truncate">{{ tpl.titolo }}</span>
                    <span class="badge bg-primary rounded-pill text-uppercase fs-8 py-1 px-2">Preset</span>
                  </div>
                  <small class="text-muted d-block text-truncate fs-7">{{ tpl.oggetto }}</small>
                </button>
              </div>

              <!-- Custom Templates Section -->
              <div class="bg-light p-2 border-bottom border-top fw-bold text-uppercase fs-7 text-muted">
                Modelli Personalizzati
              </div>
              <div class="list-group list-group-flush">
                <button
                  v-for="tpl in customTemplates"
                  :key="tpl._id"
                  class="list-group-item list-group-item-action text-start py-2 px-3 border-0 border-bottom"
                  :class="{ active: selectedTemplate && selectedTemplate._id === tpl._id }"
                  @click="loadTemplate(tpl)"
                >
                  <div class="fw-semibold text-truncate">{{ tpl.titolo }}</div>
                  <small class="text-muted d-block text-truncate fs-7">{{ tpl.oggetto }}</small>
                </button>
                <div v-if="customTemplates.length === 0" class="p-3 text-center text-muted fs-7">
                  Nessun modello personalizzato salvato.
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- MIDDLE COLUMN: Editor and Preview -->
        <div class="col-lg-5 col-md-8 mb-4">
          <div class="card shadow-sm h-100">
            <div class="card-header bg-dark text-white">
              <h5 class="mb-0 fs-6">Editor Messaggio</h5>
            </div>
            <div class="card-body d-flex flex-column">
              <!-- Template details form -->
              <div class="mb-3">
                <label class="form-label fs-7 fw-semibold text-uppercase text-muted">Titolo Modello</label>
                <input
                  type="text"
                  class="form-control form-control-sm"
                  v-model="editor.titolo"
                  placeholder="Es. Offerta di Natale, Promozione Weekend..."
                />
              </div>

              <div class="mb-3">
                <label class="form-label fs-7 fw-semibold text-uppercase text-muted">Oggetto Email</label>
                <input
                  type="text"
                  class="form-control form-control-sm"
                  v-model="editor.oggetto"
                  placeholder="Oggetto visualizzato dai clienti..."
                />
              </div>

              <div class="mb-3 flex-grow-1 d-flex flex-column">
                <label class="form-label fs-7 fw-semibold text-uppercase text-muted">Contenuto Messaggio (HTML supportato)</label>
                <textarea
                  class="form-control flex-grow-1 editor-textarea"
                  v-model="editor.messaggio"
                  placeholder="Scrivi qui il corpo dell'email... Puoi usare tag HTML per formattare."
                ></textarea>
              </div>

              <!-- Save Template Buttons -->
              <div class="d-flex justify-content-between mb-4">
                <button class="btn btn-outline-secondary btn-sm" @click="resetEditor">
                  Svuota Campi
                </button>
                <button
                  class="btn btn-primary btn-sm"
                  @click="saveTemplate"
                  :disabled="!editor.titolo || !editor.oggetto || !editor.messaggio"
                >
                  <i class="fas fa-save me-1"></i> Salva Modello
                </button>
              </div>

              <!-- Preview box -->
              <div class="border rounded bg-light p-3 preview-container">
                <div class="fs-7 fw-bold text-uppercase text-muted border-bottom pb-1 mb-2">Anteprima Email</div>
                <div class="preview-subject mb-2">
                  <strong>Oggetto:</strong> {{ editor.oggetto || '(Nessun oggetto)' }}
                </div>
                <div class="preview-body border rounded p-3 bg-white" v-html="editor.messaggio || '<em>Nessun contenuto. Scrivi nel box sopra.</em>'"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- RIGHT COLUMN: Subscribers and Manual Creation -->
        <div class="col-lg-4 col-md-12 mb-4">
          <!-- Manual subscriber addition card -->
          <div class="card shadow-sm mb-4">
            <div class="card-header bg-dark text-white py-2">
              <h6 class="mb-0 fs-6">Aggiungi Iscritto NewsLetter</h6>
            </div>
            <div class="card-body p-3">
              <form @submit.prevent="addSubscriber">
                <div class="row g-2">
                  <div class="col-sm-6">
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      placeholder="Nome"
                      v-model="newSubscriber.nome"
                    />
                  </div>
                  <div class="col-sm-6">
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      placeholder="Cognome"
                      v-model="newSubscriber.cognome"
                    />
                  </div>
                  <div class="col-sm-6">
                    <input
                      type="email"
                      class="form-control form-control-sm"
                      placeholder="Email (obbligatoria)"
                      v-model="newSubscriber.email"
                      required
                    />
                  </div>
                  <div class="col-sm-6">
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      placeholder="Telefono"
                      v-model="newSubscriber.telefono"
                    />
                  </div>
                  <div class="col-12 text-end mt-2">
                    <button type="submit" class="btn btn-success btn-sm w-100">
                      <i class="fas fa-user-plus me-1"></i> Aggiungi Iscritto
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>

          <!-- Subscribers listing and send actions -->
          <div class="card shadow-sm d-flex flex-column" style="min-height: 480px;">
            <div class="card-header bg-dark text-white py-2 d-flex justify-content-between align-items-center">
              <h6 class="mb-0 fs-6">Destinatari</h6>
              <span class="badge bg-secondary">{{ selectedCount }} Selezionati</span>
            </div>
            <div class="card-body p-3 d-flex flex-column flex-grow-1">
              <!-- Search box -->
              <div class="input-group input-group-sm mb-3">
                <span class="input-group-text bg-white border-end-0">
                  <i class="fas fa-search text-muted"></i>
                </span>
                <input
                  type="text"
                  class="form-control border-start-0"
                  placeholder="Cerca per nome o email..."
                  v-model="searchQuery"
                />
              </div>

              <!-- Selection Helpers -->
              <div class="d-flex justify-content-between mb-2">
                <div class="form-check fs-7">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="selectAllSubscribers"
                    :checked="allSelected"
                    @change="toggleSelectAll"
                  />
                  <label class="form-check-input-label fw-semibold cursor-pointer" for="selectAllSubscribers">
                    Seleziona Tutti
                  </label>
                </div>
                <button class="btn btn-link btn-xs p-0 text-decoration-none fs-7" @click="deselectAll">
                  Deseleziona
                </button>
              </div>

              <!-- List of subscribers -->
              <div class="subscriber-list flex-grow-1 border rounded bg-white overflow-auto mb-3" style="max-height: 250px;">
                <div
                  v-for="sub in filteredSubscribers"
                  :key="sub.email"
                  class="d-flex justify-content-between align-items-center p-2 border-bottom subscriber-item"
                >
                  <div class="form-check mb-0">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      :id="'sub-' + sub.email"
                      :value="sub.email"
                      v-model="selectedEmails"
                    />
                    <label class="form-check-label text-start" :for="'sub-' + sub.email">
                      <div class="fw-semibold subscriber-name text-truncate">
                        {{ sub.nome || sub.cognome ? `${sub.nome} ${sub.cognome}`.trim() : 'Anonimo' }}
                      </div>
                      <div class="text-muted subscriber-email text-truncate">{{ sub.email }}</div>
                    </label>
                  </div>
                  <div>
                    <button class="btn btn-link btn-sm text-danger p-0 px-1 border-0" @click="deleteSubscriber(sub.email)" title="Elimina iscritto">
                      <i class="fas fa-trash-alt"></i>
                    </button>
                  </div>
                </div>
                <div v-if="filteredSubscribers.length === 0" class="p-4 text-center text-muted fs-7">
                  Nessun iscritto trovato.
                </div>
              </div>

              <!-- Dispatch Newsletter box -->
              <div class="mt-auto border-top pt-3">
                <button
                  class="btn btn-primary w-100 py-2 d-flex justify-content-center align-items-center send-button"
                  @click="sendNewsletter"
                  :disabled="sending || selectedCount === 0 || !editor.oggetto || !editor.messaggio"
                >
                  <span v-if="sending" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  <i v-else class="fas fa-paper-plane me-2"></i>
                  <strong>Invia Newsletter</strong>
                </button>
                <div v-if="sendResult" class="alert mt-2 mb-0 py-2 px-3 text-center fs-7" :class="sendResult.success ? 'alert-success' : 'alert-danger'">
                  {{ sendResult.message }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { store } from '../store/store.js'

export default {
  name: 'Newsletter',
  data() {
    return {
      store,
      subscribers: [],
      templates: [],
      selectedTemplate: null,
      selectedEmails: [],
      searchQuery: '',
      sending: false,
      sendResult: null,

      editor: {
        _id: null,
        titolo: '',
        oggetto: '',
        messaggio: '',
        isPreset: false,
        tagFestivita: null
      },

      newSubscriber: {
        nome: '',
        cognome: '',
        email: '',
        telefono: ''
      }
    }
  },
  computed: {
    presetTemplates() {
      return this.templates.filter(tpl => tpl.isPreset)
    },
    customTemplates() {
      return this.templates.filter(tpl => !tpl.isPreset)
    },
    filteredSubscribers() {
      const query = this.searchQuery.toLowerCase().trim()
      if (!query) return this.subscribers

      return this.subscribers.filter(sub => {
        const nomeCompleto = `${sub.nome} ${sub.cognome}`.toLowerCase()
        return sub.email.toLowerCase().includes(query) || nomeCompleto.includes(query)
      })
    },
    selectedCount() {
      return this.selectedEmails.length
    },
    allSelected() {
      return this.filteredSubscribers.length > 0 && 
             this.filteredSubscribers.every(sub => this.selectedEmails.includes(sub.email))
    }
  },
  mounted() {
    this.fetchSubscribers()
    this.fetchTemplates()
  },
  methods: {
    home() {
      this.$router.push({ name: "Gestionale" })
    },
    getHeaders() {
      let token = '';
      try {
        token = localStorage.token ? JSON.parse(localStorage.token) : (localStorage.getItem('token') || '');
      } catch (e) {
        token = localStorage.getItem('token') || '';
      }
      return {
        headers: {
          authorization: `Bearer ${token}`
        }
      }
    },
    fetchSubscribers() {
      this.axios.get(this.store.path + 'newsletter/utenti', this.getHeaders())
        .then(response => {
          if (response.data && response.data.messaggio) {
            this.subscribers = response.data.messaggio
          }
        })
        .catch(err => {
          console.error("Error fetching subscribers:", err)
        })
    },
    fetchTemplates() {
      this.axios.get(this.store.path + 'newsletter', this.getHeaders())
        .then(response => {
          if (response.data && response.data.messaggio) {
            this.templates = response.data.messaggio
          }
        })
        .catch(err => {
          console.error("Error fetching templates:", err)
        })
    },
    loadTemplate(tpl) {
      this.selectedTemplate = tpl
      this.editor._id = tpl._id
      this.editor.titolo = tpl.titolo
      this.editor.oggetto = tpl.oggetto
      this.editor.messaggio = tpl.messaggio
      this.editor.isPreset = tpl.isPreset
      this.editor.tagFestivita = tpl.tagFestivita
      this.sendResult = null
    },
    resetEditor() {
      this.selectedTemplate = null
      this.editor._id = null
      this.editor.titolo = ''
      this.editor.oggetto = ''
      this.editor.messaggio = ''
      this.editor.isPreset = false
      this.editor.tagFestivita = null
      this.sendResult = null
    },
    saveTemplate() {
      const payload = {
        _id: this.editor._id,
        titolo: this.editor.titolo,
        oggetto: this.editor.oggetto,
        messaggio: this.editor.messaggio,
        isPreset: this.editor.isPreset,
        tagFestivita: this.editor.tagFestivita
      }

      this.axios.post(this.store.path + 'newsletter', payload, this.getHeaders())
        .then(response => {
          if (response.data && response.data.messaggio) {
            const saved = response.data.messaggio
            alert("Modello salvato con successo!")
            this.fetchTemplates()
            // Keep selected
            this.editor._id = saved._id
          }
        })
        .catch(err => {
          alert("Impossibile salvare il modello: " + (err.response?.data?.message || err.message))
        })
    },
    addSubscriber() {
      const payload = {
        email: this.newSubscriber.email,
        nome: this.newSubscriber.nome,
        cognome: this.newSubscriber.cognome,
        telefono: this.newSubscriber.telefono
      }

      this.axios.post(this.store.path + 'newsletter/utenti', payload, this.getHeaders())
        .then(response => {
          if (response.data) {
            alert("Nuovo iscritto registrato correttamente!")
            this.fetchSubscribers()
            // Reset form
            this.newSubscriber.nome = ''
            this.newSubscriber.cognome = ''
            this.newSubscriber.email = ''
            this.newSubscriber.telefono = ''
          }
        })
        .catch(err => {
          alert("Errore nella registrazione dell'iscritto: " + (err.response?.data?.message || err.message))
        })
    },
    deleteSubscriber(email) {
      if (confirm(`Sei sicuro di voler rimuovere l'iscritto con email ${email}?`)) {
        this.axios.delete(this.store.path + `newsletter/utenti/${encodeURIComponent(email)}`, this.getHeaders())
          .then(() => {
            // Remove from selected lists
            this.selectedEmails = this.selectedEmails.filter(e => e !== email)
            this.fetchSubscribers()
          })
          .catch(err => {
            alert("Impossibile rimuovere l'iscritto: " + (err.response?.data?.message || err.message))
          })
      }
    },
    toggleSelectAll(e) {
      const checked = e.target.checked
      const filteredEmails = this.filteredSubscribers.map(sub => sub.email)
      
      if (checked) {
        // Add filtered emails if not already present
        filteredEmails.forEach(email => {
          if (!this.selectedEmails.includes(email)) {
            this.selectedEmails.push(email)
          }
        })
      } else {
        // Remove only filtered emails from selection
        this.selectedEmails = this.selectedEmails.filter(email => !filteredEmails.includes(email))
      }
    },
    deselectAll() {
      this.selectedEmails = []
    },
    sendNewsletter() {
      if (this.selectedCount === 0) {
        alert("Seleziona almeno un destinatario prima di inviare!")
        return
      }

      const confirmed = confirm(`Confermi l'invio dell'email a ${this.selectedCount} destinatari?`)
      if (!confirmed) return

      this.sending = true
      this.sendResult = null

      const payload = {
        oggetto: this.editor.oggetto,
        messaggio: this.editor.messaggio,
        destinatari: this.selectedEmails
      }

      this.axios.post(this.store.path + 'newsletter/send', payload, this.getHeaders())
        .then(response => {
          this.sendResult = {
            success: true,
            message: response.data.messaggio || `Newsletter inviata con successo!`
          }
          // Clear selections on success
          this.selectedEmails = []
        })
        .catch(err => {
          this.sendResult = {
            success: false,
            message: "Errore durante l'invio: " + (err.response?.data?.message || err.message)
          }
        })
        .finally(() => {
          this.sending = false
        })
    }
  }
}
</script>

<style scoped>
.m-cont {
  background-color: #f4f7f6;
  min-height: 100vh;
}

.nav-sale {
  color: white;
  background-color: #2c3e50;
  padding: 12px 15px;
  font-weight: bold;
  font-size: 1.1rem;
}

.navbar-title {
  letter-spacing: 1px;
}

/* Custom list layout */
.list-container {
  max-height: 70vh;
  overflow-y: auto;
}

.fs-7 {
  font-size: 0.85rem !important;
}

.fs-8 {
  font-size: 0.72rem !important;
}

.editor-textarea {
  font-family: 'Courier New', Courier, monospace;
  font-size: 0.9rem;
  min-height: 250px;
  resize: vertical;
}

/* Scroll style */
.overflow-auto::-webkit-scrollbar,
.list-container::-webkit-scrollbar {
  width: 5px;
}

.overflow-auto::-webkit-scrollbar-thumb,
.list-container::-webkit-scrollbar-thumb {
  background-color: #bbb;
  border-radius: 4px;
}

.overflow-auto::-webkit-scrollbar-track,
.list-container::-webkit-scrollbar-track {
  background-color: #eee;
}

/* Preview Box Style */
.preview-container {
  box-shadow: inset 0 1px 2px rgba(0,0,0,0.05);
}

.preview-subject {
  font-size: 0.9rem;
}

.preview-body {
  max-height: 200px;
  overflow-y: auto;
  font-size: 0.85rem;
}

/* Subscriber List layout */
.subscriber-item {
  transition: background-color 0.2s;
}

.subscriber-item:hover {
  background-color: #f8f9fa;
}

.subscriber-name {
  font-size: 0.88rem;
  max-width: 220px;
}

.subscriber-email {
  font-size: 0.75rem;
  max-width: 220px;
}

.cursor-pointer {
  cursor: pointer;
}

.send-button {
  background: linear-gradient(135deg, #0d6efd, #0b5ed7);
  border: none;
  transition: all 0.3s;
}

.send-button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(13, 110, 253, 0.3);
}

.send-button:active:not(:disabled) {
  transform: translateY(0);
}

.sci-view-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--sci-bg, #f8fafc);
  color: var(--sci-text-main, #0f172a);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}

/* Header */
.sci-header {
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 28px;
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--sci-border, #e2e8f0);
}

.sci-header-left {
  display: flex;
  align-items: center;
  gap: 18px;
}

.sci-btn-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 16px;
  background: #ffffff;
  border: 1px solid var(--sci-border, #e2e8f0);
  border-radius: 12px;
  color: var(--sci-text-muted, #475569);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.22, 1, 0.36, 1);
}

.sci-btn-back:hover {
  background: #f1f5f9;
  color: var(--sci-text-main, #0f172a);
  border-color: #cbd5e1;
}

.sci-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
}

.sci-subtitle {
  margin: 2px 0 0 0;
  font-size: 0.82rem;
  color: var(--sci-text-muted, #475569);
}
</style>
